import { AfterViewInit, Component, ElementRef, OnInit, inject, viewChild, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import Quill from 'quill';

import { FormlyFieldBaseComponent } from '../formly-field-base.component';
import { MenuService } from '../../../services';
import { NarrativeRichTextMarker, NarrativeSizes } from '../../../constants';

/**
 * A narrative (notes) field with formatting: bold, italic, underline, bullet and numbered
 * lists, indent. Used where the text prints on a document and the user needs to structure it.
 *
 * ⚠️ Storage contract (see `narrative.ts`, mirrored from the API's `NarrativeConstants`):
 * the value written to the model is the editor's HTML prefixed with the rich-text marker.
 * The marker is how the server and the document renderer know this is HTML rather than legacy
 * plain text — without it the value would be encoded and printed as visible tags.
 *
 * ⚠️ Quill builds its own DOM inside this component, and Angular's view encapsulation only
 * stamps its scoping attribute on elements written in the template. So the toolbar and editor
 * CANNOT be styled from this component's stylesheet — that styling lives in
 * `tms-lib/src/lib/styles/_narrative-editor.scss`, which is global.
 */
@Component({
  selector: 'app-formly-rich-text',
  templateUrl: './formly-rich-text.component.html',
  styleUrl: './formly-rich-text.component.scss',
  imports: [FormsModule, ReactiveFormsModule, FormlyModule],
})
export class FormlyRichTextComponent extends FormlyFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly menuService = inject(MenuService);

  private readonly editorElement = viewChild<ElementRef<HTMLDivElement>>('editor');

  private quill: Quill;

  /** True while we are writing the control's value INTO the editor, so we don't echo it back. */
  private loading = false;

  /**
   * The control value the editor is currently showing. Comparing against it is what stops the
   * `valueChanges` subscription below from fighting the user's typing: our own writes are already
   * reflected, so only a change from somewhere ELSE reloads the editor.
   */
  private reflected = '';

  constructor() {
    super();

    this.defaultOptions = {
      props: { rows: 5 },
    };
  }

  ngOnInit() {
    if (this.menuService.isReadOnly) {
      this.props.readonly = true;
    }
  }

  ngAfterViewInit() {
    const element = this.editorElement()?.nativeElement;
    if (!element) {
      return;
    }

    // Hyperlinks are gated per FIELD (props.links), not on for everyone: only email-body
    // fields may carry them, because only the email allowlist (EmailBodyConstants, server
    // side) keeps them on save. `formats` must gate too, or a paste smuggles a link into a
    // narrative that the server then strips silently.
    const links = !!this.props.links;

    this.quill = new Quill(element, {
      theme: 'snow',
      readOnly: !!this.props.readonly,
      // Restrict the formats as well as the toolbar. The toolbar governs the buttons; `formats`
      // governs what the document can hold at all, so a paste cannot smuggle in a format the
      // server would only strip again on save.
      formats: ['bold', 'italic', 'underline', 'list', 'indent', 'size', ...(links ? ['link'] : [])],
      modules: {
        toolbar: this.props.readonly
          ? false
          : [
              ['bold', 'italic', 'underline'],
              // `false` is the Normal option — it REMOVES the size class rather than adding one,
              // which is why 'normal' is not one of NarrativeSizes. Quill's own stylesheet supplies
              // the Normal / Small / Large / Huge labels for this picker.
              [{ size: [false, ...NarrativeSizes] }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ indent: '-1' }, { indent: '+1' }],
              ...(links ? [['link']] : []),
              ['clean'],
            ],
      },
    });

    this.loadControlValue();

    this.quill.on('text-change', () => {
      if (!this.loading) {
        this.writeControlValue();
      }
    });

    // ⚠️ The model arrives AFTER this runs. `app-base-edit-form` fetches it via
    // `baseService.edit(id)`, so at ngAfterViewInit the control is still empty and the load above
    // put nothing in the editor. A plain <textarea> never notices because [formControl] keeps it in
    // sync; an editor written to imperatively has to watch for the value landing, or reopening a
    // saved narrative shows an empty box. (Same shape as formly-warp-weft-plan waiting for its
    // stored rows.)
    this.formControl.valueChanges.pipe(this.untilDestroyed()).subscribe((value: string) => {
      if ((value ?? '') !== this.reflected) {
        this.loadControlValue();
      }
    });
  }

  ngOnDestroy() {
    // Quill has no destroy(); dropping the reference is enough — the DOM it built goes with the
    // component's own element.
    this.quill = undefined;
  }

  /**
   * Puts the stored value into the editor. A MARKED value is HTML; anything else is legacy plain
   * text and is inserted as TEXT, so a note that happens to contain angle brackets shows the
   * characters the user typed rather than being interpreted as markup.
   */
  private loadControlValue() {
    const value: string = this.formControl.value ?? '';

    this.reflected = value;
    this.loading = true;

    if (value.startsWith(NarrativeRichTextMarker)) {
      this.quill.clipboard.dangerouslyPasteHTML(value.slice(NarrativeRichTextMarker.length), 'silent');
    } else {
      this.quill.setText(value, 'silent');
    }

    this.loading = false;
  }

  /**
   * Writes the editor's HTML back to the form control, marked as rich text. An empty editor
   * writes an empty string rather than Quill's placeholder paragraph, so the field reads as blank
   * everywhere — the form's dirty check, `Show()` on the document, and the server's own
   * "nothing visible" test all agree.
   *
   * ⚠️ `getSemanticHTML()`, NOT `quill.root.innerHTML`. The editor's own DOM renders a BULLET list
   * as `<ol><li data-list="bullet">` (verified in `formats/list.js`: `ListContainer.tagName = 'OL'`),
   * so taking the raw DOM would lose `data-list` to the sanitiser and turn every bullet list into
   * a numbered one. `getSemanticHTML()` emits real `<ul>`/`<ol>` and nests them for list indent.
   */
  private writeControlValue() {
    // ⚠️ getSemanticHTML() replaces EVERY space with `&nbsp;` (Quill's core/editor.js does
    // `escapedText.replaceAll(' ', '&nbsp;')` for each text run). A non-breaking space is not a
    // line-break opportunity, so a narrative saved as-is becomes ONE unbreakable line: on a
    // document the content then grows wider than the page, Chromium scales the whole page down to
    // fit, and every other line on it prints smaller. Nothing errors and the narrative itself
    // looks right, which is why it presents as "the font changed on the whole document".
    //
    // The narrative containers are `white-space: pre-wrap`, so ordinary spaces are preserved
    // anyway and nothing is lost by putting them back. The server does the same on save and on
    // render (`WithWrappableSpaces`) — this is the fix at source, that is the safety net and the
    // repair for narratives already stored.
    const html = this.quill
      .getSemanticHTML()
      .replace(/&nbsp;/g, ' ')
      .trim();
    const hasText = this.quill.getText().trim().length > 0;
    const value = hasText ? NarrativeRichTextMarker + html : '';

    this.reflected = value;
    this.formControl.setValue(value);
    this.formControl.markAsDirty();
  }
}
